// client/src/utils/licenseStatusUtils.js

/**
 * License status enumeration
 */
export const LicenseStatus = {
  ACTIVE: "active",
  EXPIRED: "expired",
  EXPIRING_SOON: "expiring_soon",
  NOT_FOUND: "not_found",
  INACTIVE: "inactive",
  PENDING: "pending",
};

/**
 * License status display configurations
 */
export const LicenseStatusConfig = {
  [LicenseStatus.ACTIVE]: {
    label: "Active",
    color: "success",
    severity: "success",
    icon: "CheckCircle",
    description: "License is active and valid",
  },
  [LicenseStatus.EXPIRED]: {
    label: "Expired",
    color: "error",
    severity: "error",
    icon: "Error",
    description: "License has expired",
  },
  [LicenseStatus.EXPIRING_SOON]: {
  label: "Expiring Soon",
  color: "warning", 
  severity: "warning",
  icon: "Warning",
  description: "License expires today or within 10 days",
  },
  [LicenseStatus.NOT_FOUND]: {
    label: "No License",
    color: "default",
    severity: "info",
    icon: "Info",
    description: "No license found for this product",
  },
  [LicenseStatus.INACTIVE]: {
    label: "Inactive",
    color: "default",
    severity: "warning",
    icon: "Block",
    description: "License is inactive",
  },
  [LicenseStatus.PENDING]: {
    label: "Pending",
    color: "default",
    severity: "info",
    icon: "Schedule",
    description: "License approval pending",
  },
};

/**
 * Calculate the license status based on license data
 * @param {object} license - License object
 * @returns {string} - License status
 */
export const calculateLicenseStatus = (license) => {
  if (!license) {
    return LicenseStatus.NOT_FOUND;
  }

  // Check if license is inactive
  if (license.is_active === false) {
    return LicenseStatus.INACTIVE;
  }

  // Check if license has no key (pending)
  if (!license.license_key) {
    return LicenseStatus.PENDING;
  }

  // Check expiry
  if (license.expiry_date) {
    const now = new Date();
    const expiryDate = new Date(license.expiry_date);
    
    // Set expiry date to end of day (23:59:59.999) to handle same-day expiry correctly
    const endOfExpiryDay = new Date(expiryDate);
    endOfExpiryDay.setHours(23, 59, 59, 999);
    
    // Calculate days difference properly
    const nowStartOfDay = new Date(now);
    nowStartOfDay.setHours(0, 0, 0, 0);
    
    const expiryStartOfDay = new Date(expiryDate);
    expiryStartOfDay.setHours(0, 0, 0, 0);
    
    const daysDiff = Math.ceil((expiryStartOfDay.getTime() - nowStartOfDay.getTime()) / (1000 * 3600 * 24));

    // If license has actually expired (past end of expiry day)
    if (now.getTime() > endOfExpiryDay.getTime()) {
      return LicenseStatus.EXPIRED;
    }

    // If expiring today (same day) or within 10 days
    if (daysDiff <= 10) {
      return LicenseStatus.EXPIRING_SOON;
    }
  }

  return LicenseStatus.ACTIVE;
};

/**
 * Get license status display information
 * @param {string} status - License status
 * @returns {object} - Display configuration
 */
export const getLicenseStatusDisplay = (status) => {
  return LicenseStatusConfig[status] || LicenseStatusConfig[LicenseStatus.NOT_FOUND];
};

/**
 * Get days until expiry
 * @param {object} license - License object
 * @returns {number|null} - Days until expiry, null if no expiry date
 */
export const getDaysUntilExpiry = (license) => {
  if (!license || !license.expiry_date) {
    return null;
  }

  const now = new Date();
  const expiryDate = new Date(license.expiry_date);
  
  // Calculate days difference properly - comparing start of days
  const nowStartOfDay = new Date(now);
  nowStartOfDay.setHours(0, 0, 0, 0);
  
  const expiryStartOfDay = new Date(expiryDate);
  expiryStartOfDay.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.ceil((expiryStartOfDay.getTime() - nowStartOfDay.getTime()) / (1000 * 3600 * 24));
  
  // If license expires today, show as 0 days (expiring today)
  // If license expired yesterday or before, show negative days
  return daysDiff;
};

/**
 * Format expiry date for display
 * @param {object} license - License object
 * @returns {string} - Formatted expiry date
 */
export const formatExpiryDate = (license) => {
  if (!license || !license.expiry_date) {
    return "No expiry date";
  }

  return new Date(license.expiry_date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Check if license needs attention (expired or expiring soon)
 * @param {string} status - License status
 * @returns {boolean} - True if needs attention
 */
export const licenseNeedsAttention = (status) => {
  return [LicenseStatus.EXPIRED, LicenseStatus.EXPIRING_SOON].includes(status);
};
